import { modelHelperModule } from './model-helper';
import * as StringUtils from '../utils/string-utils';
import { fieldToLabel } from '../utils/string-utils';
import { defineValue } from '../utils/object-utils/object-utils';
import { appAlertComponentModule } from '../components/app-alert/app-alert';

export const teamModelModule = angular
  .module('Q.models.team', [modelHelperModule, appAlertComponentModule])

  .factory('Team', function (ModelHelper, $q, appAlert) {
    const Team = ModelHelper.defineResource({
      name: 'Team',
      endpoint: 'teams',

      computed: {
        isSampleTeam: /* @ngInject */ function (isPracticeTeam, isDemoTeam) {
          return isPracticeTeam || isDemoTeam;
        },
      },

      relations: {
        belongsTo: {
          Customer: {
            localField: 'customer',
            localKey: 'customerId',
            link: false,
          },
        },
        hasMany: {
          Assessment: {
            localField: 'assessments',
            foreignKey: 'teamId',
          },
          TeamMember: {
            localField: 'teamMembers',
            foreignKey: 'teamId',
          },
        },
      },

      methods: {
        hasManualFeature(feature) {
          return !!(this.manualFeatures && this.manualFeatures[feature]);
        },
        hasFeature(feature, featureLevels) {
          if (featureLevels) {
            if (!Array.isArray(featureLevels)) {
              featureLevels = [featureLevels];
            }
            feature = this.getFeature(feature);
            return featureLevels.some((f) => f === feature);
          } else {
            return !!this.getFeature(feature);
          }
        },
        labKeys() {
          return Object.keys(this.labs || {}).filter((k) =>
            this.customer.getFeature(k),
          );
        },
        getFeature(feature) {
          if (!feature) {
            return true;
          }
          if (feature === 'labs') {
            // see if we have access to any labs features
            return this.labKeys().length;
          }

          let f = this.manualFeatures && this.manualFeatures[feature];
          if (f) {
            return f;
          }

          // check if the feature is in labs
          f = this.labs && this.labs[feature];
          if (typeof f === 'boolean') {
            return f && this.customer.getFeature(feature);
          }

          return this.customer.getFeature(feature);
        },
        isNewTeam() {
          // 604,800,000 = 7 days in milliseconds
          let sevenDaysAgo = Date.now() - 604800000;
          return (
            new Date(this.createdAt || new Date()).getTime() > sevenDaysAgo
          );
        },
        /**
         * Returns true if the team is allowed to use credits
         */
        usageAllowed() {
          return this.customer.isActive;
        },
        /**
         * Returns true if the team hasn't had any candidate submissions yet,
         * unless they already completed setup
         * Used mainly to know when to switch to the setup guide screen
         * @returns {boolean}
         */
        accountNeedsSetup(teamMember) {
          if (teamMember && !teamMember.hasRole('reviewer')) return false;
          let progress = this.progressEvents || {};
          return !this.setupComplete && !progress.candidateInvited;
        },
        subscriptionExpired(teamMember) {
          if (teamMember && teamMember.role === 'staff') return false;
          return this.customer.subscriptionExpired(teamMember);
        },
        isSubscribed() {
          return this.customer.isSubscribed;
        },
        isManualSubscriber() {
          return !!this.customer.currentContractId;
        },
        isSystemTeam() {
          return !!this.customer.isNonBillable;
        },
        payAsYouGo() {
          return this.customer.isPayg();
        },
        canRenew() {
          return true;
        },
        hasQualifiedTrial() {
          return this.customer.isInTrial();
        },
        hasQualifiedPlan() {
          return this.customer.hasQualifiedPlan();
        },
        isEducation() {
          return (
            this.productTypes &&
            !this.productTypes.recruitment &&
            this.productTypes.education
          );
        },
        isQualifiedLoginAllowed() {
          return this.loginProviders.includes('qualified');
        },
        usageMeasureName(plural) {
          switch (this.customer.usageMeasure()) {
            case 'assessment_result':
              return plural ? 'Assessment Results' : 'Assessment Result';

            case 'team_candidate':
              if (this.isEducation()) {
                return plural ? 'Student' : 'Students';
              }

              return plural ? 'Candidate' : 'Candidates';
          }
        },
        /**
         * Returns the collection of all known tags for this team
         */
        findAllTags() {
          const self = this;
          return $q.resolve().then(() => {
            if (!self._tags) {
              return Team.tags(this).then((tags) => {
                let rootTags = {};
                angular.forEach(tags, (modelTags) => {
                  angular.forEach(modelTags, (typeTags, type) => {
                    if (Array.isArray(typeTags)) {
                      rootTags[type] = rootTags[type] || [];
                      typeTags.forEach((t) => {
                        if (rootTags[type].indexOf(t) === -1)
                          rootTags[type].push(t);
                      });
                    }
                  });
                });
                angular.forEach(rootTags, (rt, type) => {
                  tags[type] = rt;
                });
                defineValue(self, '_tags', tags);
                return tags;
              });
            } else {
              return self._tags;
            }
          });
        },
        /**
         * Returns a matching set of tags for this team
         * @param type {String} currently either `labels` or `topics`
         * @param model {String?} if provided, only return types for the given model
         */
        findTags(type, model) {
          return this.findAllTags().then((tags) => {
            let tagsList = tags[type];
            if (model) {
              tagsList = tags[model] && tags[model][type];
            }
            tagsList = tagsList || [];
            return tagsList.sort(StringUtils.compareIgnoreCase);
          });
        },
        /**
         * Updates the existing tags (if we've already downloaded them)
         * @param model {String} Model type being updated
         * @param type {String} Tag type (labels or topics)
         * @param tags {Array<String>} Updated list of tags
         */
        updateTags(model, type, tags) {
          if (this._tags && tags && tags.length) {
            const add = (to) => {
              tags.forEach((t) => {
                if (to.indexOf(t) === -1) {
                  to.push(t);
                }
              });
            };
            if (!this._tags[model]) this._tags[model] = {};
            if (!this._tags[model][type]) this._tags[model][type] = [];
            add(this._tags[model][type], tags);
            if (!this._tags[type]) this._tags[type] = [];
            add(this._tags[type], tags);
          }
        },

        flagProgressEvent(progressEvent) {
          const data = { progressEvent };
          Team.flagProgressEvent(this, { data });
        },
        customerState() {
          return (
            fieldToLabel((this.customer && this.customer.state) || '') || '—'
          );
        },

        disableWarnOnLongAssessments() {
          this.warnOnLongAssessments = false;
          return Team.update(this, { warnOnLongAssessments: false }).catch(
            appAlert.modelError,
          );
        },
      },

      staticMethods: {
        progressFlags: {
          demoCandidateViewed: 'demo_data_candidate_viewed',
          demoPlaybackViewed: 'demo_data_playback_viewed',
          demoReviewViewed: 'demo_data_code_review_viewed',
        },
      },

      meta: {
        actionEndpoints: {
          generateApiKey: { method: 'PUT' },
          buildDemoData: {
            method: 'PUT',
            responseHandler(res, TeamMember, instance) {
              instance.buildingDemoData = true;
              return res;
            },
          },
          destroyDemoData: {
            method: 'PUT',
            responseHandler(res, TeamMember, instance) {
              instance.destroyingDemoData = true;
              return res;
            },
          },
          flagProgressEvent: { method: 'PUT' },
          signMsa: { method: 'PUT', track: true },
          updateBilling: { method: 'PUT', track: true },
          subscribe: { method: 'PUT', track: true },
          renewSubscription: { method: 'PUT', track: true },
          endTrial: { method: 'PUT', track: true },
          bambooSubdomain: { method: 'PUT', track: true },
          leverTriggerStage: { method: 'PUT', track: true },
          resetLever: { method: 'PUT', track: true },
          tags: {
            method: 'GET',
            responseHandler: (resp) => {
              const data = resp && resp.data;
              return data.data || data;
            },
          },
        },
        saveExcludes: ['assessments', 'teamMembers'],
      },
    });

    return Team;
  });
